import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AmountUtils, cn, ColorUtils, DateUtils, FormatUtils } from '@shared/utils';
import { type GetDepositPaymentsResponse } from '@shared/api';
import { Table, TableCellType } from '@shared/widgets';

interface DepositTransactionTableProps {
  isLoading?: boolean;
  error?: string | null;
  onRefresh?: () => void;
  columns: string[];
  items: GetDepositPaymentsResponse[];
  currentPage: number;
  totalItemCount: number;
  countItemPage: number;
  onChangePage: (pageNumber: number) => void;
  onChangeCountItemPage: (pageNumber: number) => void;
}
export const DepositTransactionTable = (props: DepositTransactionTableProps) => {
  const { t } = useTranslation('reports');

  const cells: Record<
    string,
    (row: GetDepositPaymentsResponse) => {
      type: TableCellType;
      value: React.ReactNode;
    }
  > = useMemo(
    () => ({
      Status: (row: GetDepositPaymentsResponse) => {
        const color = ColorUtils.getDepositPaymentStatusStyles(row.status);
        return {
          type: TableCellType.Custom,
          value: (
            <div className={cn('whitespace-nowrap px-2 py-1 text-center font-normal', color.bg, color.text)}>
              {t(`statuses.paymentTransaction.${row.status}`, { ns: 'common' })}
            </div>
          ),
        };
      },
      ReferenceNumber: (row: GetDepositPaymentsResponse) => ({
        type: TableCellType.Text,
        value: row.referenceNumber,
      }),
      Date: (row: GetDepositPaymentsResponse) => ({
        type: TableCellType.Text,
        value: DateUtils.formatDate(row.valueDate),
      }),
      AccountNumber: (row: GetDepositPaymentsResponse) => ({
        type: TableCellType.Text,
        value: row.accountNumber,
      }),
      Direction: (row: GetDepositPaymentsResponse) => ({
        type: TableCellType.Text,
        value: t(`direction.${row.direction.toLowerCase()}`, { ns: 'common' }),
      }),
      TypeOfTransfer: (row: GetDepositPaymentsResponse) => ({
        type: TableCellType.Text,
        value: FormatUtils.transactionTypeTransformation(t(`transferRequestType.${row.typeOfTransfer}`, { ns: 'common' })),
      }),
      Description: (row: GetDepositPaymentsResponse) => ({
        type: TableCellType.Text,
        value: row.description,
      }),
      SenderIban: (row: GetDepositPaymentsResponse) => ({
        type: TableCellType.Text,
        value: row.senderIban,
      }),
      BeneficiaryIban: (row: GetDepositPaymentsResponse) => ({
        type: TableCellType.Text,
        value: row.beneficiaryIban,
      }),
      ExchangeRate: (row: GetDepositPaymentsResponse) => ({
        type: TableCellType.Text,
        value: `${AmountUtils.transformToAmountWithCurrency(1, row.currencyIn)} = ${AmountUtils.transformToAmountWithCurrency(row.exchangeRate, row.currencyOut)}`,
      }),
      SenderAmount: (row: GetDepositPaymentsResponse) => ({
        type: TableCellType.Text,
        value: AmountUtils.transformToAmountWithCurrency(row.senderAmount, row.currencyIn),
      }),
      BeneficiaryAmount: (row: GetDepositPaymentsResponse) => ({
        type: TableCellType.Text,
        value: AmountUtils.transformToAmountWithCurrency(row.beneficiaryAmount, row.currencyOut),
      }),
      Fee: (row: GetDepositPaymentsResponse) => ({
        type: TableCellType.Text,
        value: AmountUtils.transformToAmountWithCurrency(row.fee, row.currency),
      }),
      TotalAmount: (row: GetDepositPaymentsResponse) => ({
        type: TableCellType.Text,
        value: AmountUtils.transformToAmountWithCurrency(row.totalAmount, row.currency),
      }),
    }),
    [t],
  );
  const items = useMemo(() => {
    return props.items.map((row) => {
      return props.columns.reduce((acc: { type: TableCellType; value: React.ReactNode }[], col) => {
        const cell = cells[col]?.(row);
        cell && acc.push(cell);
        return acc;
      }, []);
    });
  }, [cells, props.columns, props.items]);
  return (
    <Table
      isScrollable
      isLoading={props.isLoading}
      error={props.error}
      onRefresh={props.onRefresh}
      columns={props.columns.map((column) => t(column))}
      items={items}
      pagination={{
        currentPage: props.currentPage,
        totalItemCount: props.totalItemCount,
        countItemPage: props.countItemPage,
        onChangePage: props.onChangePage,
        onChangeCountItemPage: props.onChangeCountItemPage,
      }}
    />
  );
};
